import { Injectable, signal, OnDestroy, effect } from '@angular/core';
import { AudioRecording } from '../models/mic.models';
import { MicStatus } from '../models/mic.models';
import { MicStateService } from '../components/mic/mic-state.service';

@Injectable({
  providedIn: 'root',
})
export class MicRecordingService implements OnDestroy {
  // States
  public rms = signal<number>(0);
  public status = signal<MicStatus>(MicStatus.Inactive);
  public recordedAudioBlob = signal<AudioRecording | null>(null);
  private firstNoise: boolean = false;

  // Components for recording for silence detection
  public micStream: MediaStream | null = null; // acceso al microphono
  public mediaRecorder: MediaRecorder | null = null; // flujo que graba el audio
  public audioWorkletNode: AudioWorkletNode | null = null; // procesamiento del audio para detectar silencios
  public microphoneSource: MediaStreamAudioSourceNode | null = null; // fuente de audio del microphono
  private audioContext: AudioContext | null = null;
  private audioWorkletPromise: Promise<void> | null = null;
  private currentRecordingMode: 'silenceDetection' | 'continuous' | null = null;

  // Unified properties (formerly some were for silence detection only)
  private timeoutInstance: any = null; // Timer for silence detection
  private audioChunks: BlobPart[] = []; // Stores audio data chunks for all recording types

  constructor(private micStateService: MicStateService) {
    effect(() => {
      this.micStateService.changeMicStatus(this.status());
    });
  }

  ngOnDestroy(): void {
    if (this.audioContext && this.audioContext.state !== 'closed') {
      this.audioContext.close().catch((e) => console.error('Error closing AudioContext on destroy', e));
      this.audioContext = null;
    }
    this.stopMicrophone();
  }

  stopMicrophone() {
    if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') {
      this.mediaRecorder.stop(); // This will trigger its onstop, which calls _cleanupMediaResources and sets signals
    } else if (this.status() !== MicStatus.Inactive) {
      // If not actively recording, but status is not Inactive (e.g., was Listening or error during setup)
      // or if resources might exist due to an incomplete setup.
      console.log('Mic was not actively recording or in an intermediate state, ensuring cleanup.');
      this._cleanupMediaResources(); // Ensure cleanup if resources were partially initialized.
      this.status.set(MicStatus.Inactive); // Explicitly set status if not done by onstop.
    }
    this.recordedAudioBlob.set(null);

    // Extra precaution for silence timer, though _cleanupMediaResources should handle it.
    if (this.timeoutInstance) {
      clearTimeout(this.timeoutInstance);
      this.timeoutInstance = null;
    }
  }

  private _cleanupMediaResources() {
    // Renamed from _cleanupSilenceDetectionResources
    if (this.timeoutInstance) {
      clearTimeout(this.timeoutInstance);
      this.timeoutInstance = null;
    }
    if (this.micStream) {
      this.micStream.getTracks().forEach((track) => track.stop());
      this.micStream = null;
    }
    if (this.microphoneSource) {
      this.microphoneSource.disconnect();
      this.microphoneSource = null;
    }
    if (this.audioWorkletNode) {
      if (this.audioWorkletNode.port) {
        this.audioWorkletNode.port.onmessage = null; // Remove event listener
      }
      this.audioWorkletNode.disconnect();
      this.audioWorkletNode = null;
    }
    if (this.mediaRecorder) {
      // Ensure event listeners are removed to prevent memory leaks
      this.mediaRecorder.onstop = null;
      this.mediaRecorder.onerror = null;
      this.mediaRecorder.ondataavailable = null;
      this.mediaRecorder = null; // Nullify the MediaRecorder instance
    }
    this.audioChunks = [];
    this.firstNoise = false;
    this.currentRecordingMode = null;
    // Note: status is typically set by the caller or onstop/onerror before this cleanup.
  }

  private async _ensureAudioContextAndWorklet(): Promise<AudioContext> {
    if (!this.audioContext || this.audioContext.state === 'closed') {
      this.audioContext = new AudioContext();
      this.audioWorkletPromise = null; // Reset promise if context is new
      console.log('AudioContext created or reopened.');
    }

    if (!this.audioWorkletPromise) {
      if (!this.audioContext) {
        // Should not happen if logic above is correct
        throw new Error('AudioContext not initialized before loading worklet.');
      }
      console.log('Loading audio worklet module...');
      this.audioWorkletPromise = this.audioContext.audioWorklet
        .addModule('assets/js/rms-processor.js')
        .then(() => {
          console.log('Audio worklet module loaded successfully.');
        })
        .catch((workletError) => {
          console.error('Failed to load audio worklet module:', workletError);
          this.audioWorkletPromise = null; // Allow retry
          // Propagate the error to be handled by the calling method
          throw workletError;
        });
    }
    await this.audioWorkletPromise;
    // After await, this.audioContext could theoretically have been closed by another process.
    // Re-check, though in a typical Angular service, this is less likely.
    if (!this.audioContext || this.audioContext.state === 'closed') {
      throw new Error('AudioContext was closed during worklet loading.');
    }
    return this.audioContext;
  }

  private async _initializeMediaResources(options: { enableSilenceDetection: boolean }): Promise<void> {
    if (this.status() !== MicStatus.Inactive) {
      console.warn('Recording initialization attempted while already active or initializing. Please stop first.');
      return; // Or throw an error
    }

    this.status.set(MicStatus.Listening);
    this.recordedAudioBlob.set(null);
    this.audioChunks = [];
    // Reset silence detection specific state if re-initializing
    this.firstNoise = false;
    if (this.timeoutInstance) {
      clearTimeout(this.timeoutInstance);
      this.timeoutInstance = null;
    }

    try {
      const currentAudioContext = await this._ensureAudioContextAndWorklet();

      this.micStream = await navigator.mediaDevices.getUserMedia({
        audio: { sampleRate: 48000, echoCancellation: true, noiseSuppression: true },
      });

      this.mediaRecorder = new MediaRecorder(this.micStream, { audioBitsPerSecond: 128000, mimeType: 'audio/webm' });

      this.mediaRecorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) {
          this.audioChunks.push(event.data);
        }
      };

      this.mediaRecorder.onstop = () => {
        console.log('Recording stopped. Mode:', this.currentRecordingMode);
        const blob = this.audioChunks.length > 0 ? new Blob(this.audioChunks, { type: 'audio/webm' }) : null;
        // TODO check is is the best so send.
        this.recordedAudioBlob.set({
          id: '_random_id',
          timestamp: new Date(),
          url: URL.createObjectURL(blob),
          duration: this.audioChunks.length,
          blob,
        });
        this.status.set(MicStatus.Inactive);
        this._cleanupMediaResources();
      };

      this.mediaRecorder.onerror = (event) => {
        console.error('MediaRecorder error:', event);
        this.status.set(MicStatus.Inactive);
        this.recordedAudioBlob.set(null);
        this._cleanupMediaResources();
      };

      // Unconditional RMS and AudioWorklet setup
      if (!currentAudioContext) {
        // Should have been ensured by _ensureAudioContextAndWorklet, but good safeguard
        throw new Error('AudioContext not available for audio processing setup.');
      }
      this.microphoneSource = currentAudioContext.createMediaStreamSource(this.micStream);
      this.audioWorkletNode = new AudioWorkletNode(currentAudioContext, 'rms-processor');
      this.microphoneSource.connect(this.audioWorkletNode);

      this.audioWorkletNode.port.onmessage = (event) => {
        this.rms.set(event.data.rms); // Always update RMS

        // Conditional silence detection logic
        if (options.enableSilenceDetection && this.currentRecordingMode === 'silenceDetection') {
          const silenceThreshold = 0.035; // TODO: Consider making these configurable
          const silenceTimeout = 1000; // TODO: Consider making these configurable

          // Silence detection actions. Internal checks for mediaRecorder state handle cases
          // where recorder might be null or in an unsuitable state.
          if (this.firstNoise === false && this.rms() > silenceThreshold) {
            if (this.mediaRecorder && this.mediaRecorder.state === 'inactive') {
              console.log('First noise detected, starting MediaRecorder for silence detection mode...');
              this.status.set(MicStatus.Recording);
              this.firstNoise = true;
              this.mediaRecorder.start();
            }
          }

          if (this.rms() < silenceThreshold) {
            if (this.firstNoise && !this.timeoutInstance && this.mediaRecorder && this.mediaRecorder.state === 'recording') {
              console.log('Silence detected, starting silence timer...');
              this.timeoutInstance = setTimeout(() => {
                console.log('Silence timeout, stopping microphone.');
                this.stopMicrophone(); // This will trigger mediaRecorder.onstop
              }, silenceTimeout);
            }
          } else {
            // RMS is above threshold
            if (this.timeoutInstance) {
              clearTimeout(this.timeoutInstance);
              this.timeoutInstance = null;
            }
          }
        }
      };
      // this.audioWorkletNode.connect(currentAudioContext.destination); // Connect to output or analyze only
    } catch (error) {
      console.error('Error initializing media resources:', error);
      this.status.set(MicStatus.Inactive);
      this.recordedAudioBlob.set(null);
      this._cleanupMediaResources(); // Ensure cleanup on initialization error
      // Optionally re-throw or handle more gracefully
    }
  }

  async startRecordingUntilSilence(): Promise<void> {
    if (this.status() !== MicStatus.Inactive) {
      console.warn('Recording already in progress or initializing. Please stop the current recording first.');
      return;
    }

    this.currentRecordingMode = 'silenceDetection';

    try {
      await this._initializeMediaResources({ enableSilenceDetection: true });
      console.log('Recording until silence initiated. Listening for sound...');
    } catch (err) {
      console.error('Fallback error catch in startRecordingUntilSilence:', err);
      if (this.status() !== MicStatus.Inactive) {
        // Ensure status is Inactive if not already set
        this.status.set(MicStatus.Inactive);
      }
      // _cleanupMediaResources should have been called by _initializeMediaResources on error.
    }
  }

  async startContinuousRecording(): Promise<void> {
    if (this.status() !== MicStatus.Inactive) {
      console.warn('Recording already in progress or initializing. Please stop the current recording first.');
      return;
    }

    this.currentRecordingMode = 'continuous';

    try {
      await this._initializeMediaResources({ enableSilenceDetection: false });

      if (this.mediaRecorder && this.mediaRecorder.state === 'inactive') {
        // Ensure it's ready
        this.mediaRecorder.start();
        this.status.set(MicStatus.Recording); // Set status after successfully starting
        console.log('Continuous recording started.');
      } else if (this.mediaRecorder) {
        console.warn('MediaRecorder exists but is not in inactive state for continuous recording:', this.mediaRecorder.state);
      } else {
        console.error('Continuous recording could not start: MediaRecorder not initialized.');
      }
    } catch (err) {
      console.error('Fallback error catch in startContinuousRecording:', err);
      if (this.status() !== MicStatus.Inactive) {
        this.status.set(MicStatus.Inactive);
      }
    }
  }

  // Methods startRecordingOnPress and stopRecordingOnRelease have been removed as per refactoring plan.
}
